import Container from "../components/Container";
import { Helmet } from "react-helmet";

export default function About() {
    const currentUrl = typeof window !== "undefined" ? window.location.href : "https://www.credibletech.in/aboutus";
    return (
        <>
            <Helmet>
                {/* Basic Meta */}
                <title>About Us | Credible Technologies - Refurbished Laptops & Workstations in Bangalore</title>
                <meta
                    name="description"
                    content="Learn about Credible Technologies, Bangalore’s trusted source for gaming PCs, animation workstations, refurbished laptops, and expert local service. Discover our mission, values, and why creators choose us."
                />
                <meta
                    name="keywords"
                    content="About Credible Technologies, Bangalore, gaming PCs, animation workstations, refurbished laptops, laptop repair, local service, trusted source, business solutions, student laptops, business laptops, rental laptops, corporate laptops"
                />
                <meta name="robots" content="index, follow" />

                {/* Dynamic URL */}
                <link rel="canonical" href={currentUrl} />
                <meta property="og:url" content={currentUrl} />

                {/* Open Graph */}
                <meta property="og:title" content="About Us | Credible Technologies" />
                <meta
                    property="og:description"
                    content="Learn about Credible Technologies, Bangalore’s trusted source for gaming PCs, animation workstations, refurbished laptops, and expert local service."
                />
                <meta property="og:type" content="website" />
                <meta property="og:image" content="https://www.credibletech.in/assets/newlogo1.png" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content="About Us | Credible Technologies" />
                <meta
                    name="twitter:description"
                    content="Learn about Credible Technologies, Bangalore’s trusted source for gaming PCs, animation workstations, refurbished laptops, and expert local service."
                />
                <meta name="twitter:image" content="https://www.credibletech.in/assets/newlogo1.png" />

                {/* Fonts Preconnect */}
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="true" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap"
                    rel="stylesheet"
                />

                {/* Structured Data */}
                <script type="application/ld+json">{`
                    {
                    "@context": "https://schema.org",
                    "@type": "LocalBusiness",
                    "name": "Credible Technologies",
                    "image": "https://www.credibletech.in/assets/newlogo1.png",
                    "url": "${currentUrl}",
                    "telephone": "+91-9538577199",
                    "address": {
                        "@type": "PostalAddress",
                        "streetAddress": "1093, 26th B Main Rd, opposite Trends Woman, Putlanpalya, Jayanagara 9th Block, Jayanagar",
                        "addressLocality": "Bengaluru",
                        "addressRegion": "Karnataka",
                        "postalCode": "560069",
                        "addressCountry": "IN"
                    },
                    "description": "Learn about Credible Technologies, Bangalore’s trusted source for gaming PCs, animation workstations, refurbished laptops, and expert local service. Discover our mission, values, and why creators choose us.",
                    "openingHours": "Mo-Fr 09:00-18:00, Sa 10:00-16:00",
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "telephone": "+91-9538577199",
                        "contactType": "customer service",
                        "areaServed": "IN",
                        "availableLanguage": "English"
                    },
                    "sameAs": [
                        "https://www.facebook.com/credibletech",
                        "https://www.instagram.com/credibletech",
                        "https://www.linkedin.com/company/credibletech"
                    ],
                    "hasMap": "https://goo.gl/maps/your-location"
                    }
                `}</script>

                {/* Breadcrumb Schema */}
                <script type="application/ld+json">{`
                    {
                    "@context": "https://schema.org",
                    "@type": "BreadcrumbList",
                    "itemListElement": [
                        {
                        "@type": "ListItem",
                        "position": 1,
                        "name": "Home",
                        "item": "https://www.credibletech.in/"
                        },
                        {
                        "@type": "ListItem",
                        "position": 2,
                        "name": "About Us",
                        "item": "https://www.credibletech.in/aboutus"
                        }
                    ]
                    }
                `}</script>
            </Helmet>

            {/* HERO SECTION */}
            <section className="relative min-h-[60vh] flex items-center bg-gradient-to-br from-black via-slate-900 to-black text-white">
                <Container>
                    <div className="max-w-4xl">
                        <p className="uppercase tracking-widest text-blue-400 mb-3">
                            Welcome to
                        </p>
                        <h1 className="text-4xl md:text-6xl font-bold leading-tight">
                            Credible <span className="text-blue-500">Technologies</span>
                        </h1>
                        <p className="mt-6 text-lg text-slate-300 max-w-2xl">
                            We turn creativity into capability. With over{" "}
                        <span className="text-white font-semibold">
                            20+ years of industry experience
                        </span>
                            , we are your trusted end-to-end technology partner.
                        </p>
                    </div>
                </Container>
            </section>

            {/* ABOUT CONTENT */}
            <section className="relative py-24 bg-gradient-to-br from-black via-slate-900 to-black text-white overflow-hidden">

                {/* Subtle glow accents */}
                <div className="absolute -top-40 right-0 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-40 left-0 w-96 h-96 bg-indigo-500/10 rounded-full blur-3xl"></div>

                <Container>
                    <div className="max-w-5xl mx-auto">

                        {/* Heading */}
                        <div className="text-center mb-14">
                            <h2 className="text-3xl md:text-4xl font-bold text-white">
                                Who We Are
                            </h2>
                            <div className="w-20 h-1 bg-blue-600 mx-auto my-6"></div>
                            <p className="text-lg text-slate-300 max-w-3xl mx-auto">
                                A technology partner built on experience, precision, and trust.
                            </p>
                        </div>

                        {/* Content Card */}
                        <div className="bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-10 md:p-14">

                            <p className="text-slate-300 leading-relaxed mb-6">
                                At{" "}
                                <span className="font-semibold text-white">
                                    Credible Technologies
                                </span>
                                , we believe technology should empower businesses and individuals
                                to perform at their best. Our expertise spans laptop sales,
                                refurbishment, advanced repairs, and professional-grade computing
                                solutions.
                            </p>

                            <p className="font-semibold text-white text-slate-300 leading-relaxed mb-6">
                                A brief introduction about Credible Technologies.
                            </p>

                            <p className="text-slate-300 leading-relaxed mb-6">
                                We are a seasoned team with core expertise on Media & Entertainment space of 20+ years experience 
                                with strong Sales as well as Technical capabilities with fair knowledge on the products related to 
                                the 2D & 3D Graphic design domain.
                            </p>

                            <p className="text-slate-300 leading-relaxed mb-6">
                                We handle Industry verticals like{" "}
                                <span className="font-semibold text-white">
                                    Animation, Gaming, Post Production, Architectural & Interior Design, 
                                    Structural Engineering, Broadcast, Product design, E-Learning , Medical Visualisation, Virtual & Augumented 
                                </span>
                                
                                Reality and more market verticals offering End-to-End solution.
                            </p>

                            <p className="text-blue-500 font-semibold leading-relaxed mb-6">
                                We have partnered with brands like Huion, Wacom, Corel, Adobe, GstarCAD, IntelliCAD, ZWCAD, Trimble Sketchup, Revit, Autodesk (3dsmax, Maya, Motionbuilder, 
                                Mudbox, Stingray, Shotgun), Z-brush, Toonboom, Vray, Unity Game engine, Foundry Nuke, Muster, Silhoutte, Cinema 4D, several other 2D & 3D Plugins, 
                                NVIDIA Graphic cards, Apple, HP , Dell & Asus (desktops / workstations / servers), Microsoft (OS / Office / Servers / CALs ), Refurbished machines 
                                (laptops / desktops / workstations), BOXX Workstations & Render blades , Cloud based storage and Render services etc.
                            </p>

                            <p className="text-slate-300 leading-relaxed mb-6">
                                We also do rental of highend laptops, computers and Highend workstations within Bangalore.
                            </p>

                            <p className="text-slate-300 leading-relaxed mb-6">
                                With decades of hands-on industry experience, we specialize in
                                delivering dependable systems built with premium components,
                                precise engineering, and uncompromising quality standards.
                            </p>

                            <p className="text-slate-300 leading-relaxed">
                                Whether you are buying, selling, repairing, or upgrading your
                                system — Credible Technologies is committed to delivering
                                excellence at every stage of your journey.
                            </p>

                            {/* Stats */}
                            <div className="grid sm:grid-cols-3 gap-8 mt-14 text-center">

                                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                    <p className="text-3xl font-bold text-blue-500">20+</p>
                                    <p className="text-sm text-slate-400 mt-2">
                                        Years of Industry Experience
                                    </p>
                                </div>

                                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                    <p className="text-3xl font-bold text-blue-500">10K+</p>
                                    <p className="text-sm text-slate-400 mt-2">
                                        Systems Sold & Serviced
                                    </p>
                                </div>

                                <div className="bg-white/5 border border-white/10 rounded-2xl p-6">
                                    <p className="text-3xl font-bold text-blue-500">Bangalore</p>
                                    <p className="text-sm text-slate-400 mt-2">
                                        Trusted Local Expertise
                                    </p>
                                </div>

                            </div>

                        </div>
                    </div>
                </Container>
            </section>

            {/* MISSION SECTION */}
            <section className="py-20 bg-gradient-to-br from-black via-slate-900 to-black text-white">
                <div className="max-w-6xl mx-auto px-6">

                    {/* Heading */}
                    <div className="text-center mb-14">
                        <h2 className="text-3xl md:text-4xl font-bold flex items-center justify-center gap-2">
                            Our Mission
                        </h2>
                        <div className="w-20 h-1 bg-blue-500 mx-auto mt-4"></div>
                    </div>

                    {/* Content Card */}
                    <div className="bg-white/5 backdrop-blur-lg border border-white/10 rounded-3xl p-8 md:p-12 shadow-xl">

                        {/* Description */}
                        <p className="text-gray-300 text-lg leading-relaxed">
                            At <span className="text-white font-semibold">Credible Technologies</span>, Bangalore’s trusted destination for 
                            <span className="text-blue-400"> gaming PCs, animation PCs, CAD workstations, refurbished systems, Windows laptops, and Apple M devices</span>, 
                            our mission is to deliver <span className="text-white font-semibold">high-performance computers with reliable local service</span>.
                        </p>

                        <p className="text-gray-300 text-lg leading-relaxed mt-4">
                            We serve gamers, students, designers, and professionals with custom-built systems, professional workstations, 
                            and top-brand refurbished laptops from <span className="text-white font-semibold">Apple, HP, Dell, Lenovo, Asus, Acer, and MSI</span> — 
                            all backed by <span className="text-blue-400 font-semibold">1-year warranty, affordable pricing, and easy EMI options</span>.
                        </p>

                        <p className="text-gray-300 text-lg leading-relaxed mt-4">
                            Serving all of Bangalore and nearby areas, we combine quality products, expert repairs, and friendly support to ensure 
                            you get the perfect system for your <span className="text-white font-semibold">work, creativity, and gaming needs</span>.
                        </p>

                        {/* Services Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-6 mt-10">
                            {[
                                "Gaming PCs",
                                "Animation PCs",
                                "CAD Workstations",
                                "Refurbished Systems",
                                "Windows Laptops",
                                "Apple M Series",
                            ].map((item, i) => (
                                <div
                                    key={i}
                                    className="bg-white/10 rounded-xl py-4 text-center text-sm md:text-base font-semibold tracking-wide hover:bg-blue-600/20 transition"
                                >
                                    {item}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* SOLUTIONS SECTION */}
            <section className="py-24 bg-gradient-to-br from-black via-slate-900 to-black text-white relative overflow-hidden">

                {/* Soft background glow */}
                <div className="absolute -top-32 -left-32 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
                <div className="absolute bottom-0 right-0 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl"></div>

                <div className="relative max-w-7xl mx-auto px-6">

                    {/* Heading */}
                    <div className="text-center mb-16">
                        <h2 className="text-3xl md:text-4xl font-bold tracking-tight">
                            🛠️ Our Comprehensive Solutions
                        </h2>
                        <p className="text-gray-400 mt-4 text-lg max-w-2xl mx-auto">
                            From gaming to graphics, we deliver complete hardware and service solutions across Bangalore.
                        </p>
                        <div className="w-24 h-1 bg-blue-500 mx-auto mt-6 rounded-full"></div>
                    </div>

                    {/* Cards */}
                    <div className="grid md:grid-cols-2 gap-10">

                        {/* CARD */}
                        {[
                            {
                                icon: "🎮",
                                title: "Gaming & Performance Systems",
                                desc: "Custom-built gaming PCs, high-FPS laptops, RGB setups, NVIDIA & AMD GPUs, and cooling-optimized towers for serious gamers."
                            },
                            {
                                icon: "🧠",
                                title: "Workstations for Animation & CAD",
                                desc: "Powerful PCs for 3D rendering, animation, architecture, and engineering — optimized for Maya, AutoCAD, Blender & Revit."
                            },
                            {
                                icon: "💻",
                                title: "Top Brand Laptops & Desktops",
                                desc: "Apple MacBooks, HP, Dell, Lenovo, Asus, Acer, MSI — new and certified refurbished systems with warranty."
                            },
                            {
                                icon: "🔧",
                                title: "Sales, Service & Upgrades",
                                desc: "Laptop repair from ₹150, OS installation, RAM & SSD upgrades, GPU replacement, and full diagnostics."
                            },
                        ].map((item, index) => (
                            <div
                                key={index}
                                className="group relative bg-white/5 backdrop-blur-xl border border-white/10 rounded-3xl p-8 shadow-xl transition-all duration-500 hover:-translate-y-2 hover:border-blue-500/40"
                            >
                            {/* Glow on hover */}
                                <div className="absolute inset-0 rounded-3xl bg-blue-500/10 opacity-0 group-hover:opacity-100 blur-xl transition"></div>

                                <div className="relative z-10">
                                    <div className="text-5xl mb-5">{item.icon}</div>
                                    <h3 className="text-xl font-bold mb-3">
                                        {item.title}
                                    </h3>
                                    <p className="text-gray-300 leading-relaxed">
                                        {item.desc}
                                    </p>
                                </div>
                            </div>
                        ))}

                    </div>
                </div>
            </section>

            {/* WHY CREATORS CHOOSE US */}
            <section className="relative py-24 bg-gradient-to-br from-black via-slate-900 to-black text-white overflow-hidden">

                {/* Soft glow accents */}
                <div className="absolute -top-32 right-0 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-32 left-0 w-96 h-96 bg-indigo-500/20 rounded-full blur-3xl"></div>

                <Container>
                    {/* Heading */}
                    <div className="max-w-3xl mx-auto text-center mb-16">
                    <h2 className="text-3xl md:text-4xl font-bold text-white">
                        Why Creators Choose Us
                    </h2>
                    <p className="mt-4 text-lg text-slate-300">
                        Our dedication to quality and deep industry knowledge makes us the trusted
                        partner for creative professionals across Bangalore.
                    </p>
                    </div>

                    {/* Grid */}
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">

                    {/* Card 1 */}
                    <div className="group bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-3xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all">
                        <div className="mb-5 w-12 h-12 rounded-xl bg-blue-500/20 text-blue-500 flex items-center justify-center text-xl font-bold">
                            🎨
                        </div>
                        <h3 className="text-xl font-semibold mb-3 text-white">
                            Creative-First Approach
                        </h3>
                        <p className="text-slate-300 leading-relaxed">
                            We understand your workflow and speak your language — from storyboard
                            planning to final render delivery.
                        </p>
                    </div>

                    {/* Card 2 */}
                    <div className="group bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-3xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all">
                        <div className="mb-5 w-12 h-12 rounded-xl bg-indigo-500/20 text-indigo-500 flex items-center justify-center text-xl font-bold">
                            🧠
                        </div>
                        <h3 className="text-xl font-semibold mb-3 text-white">
                            Tech-Savvy Team
                        </h3>
                        <p className="text-slate-300 leading-relaxed">
                            Specialists in gaming, design, animation, CAD, and engineering software
                            & hardware ecosystems.
                        </p>
                    </div>

                    {/* Card 3 */}
                    <div className="group bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-3xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all">
                        <div className="mb-5 w-12 h-12 rounded-xl bg-emerald-500/20 text-emerald-500 flex items-center justify-center text-xl font-bold">
                            🛠️
                        </div>
                        <h3 className="text-xl font-semibold mb-3 text-white">
                            Custom Solutions
                        </h3>
                        <p className="text-slate-300 leading-relaxed">
                            Tailor-made PCs, laptops, and workstations designed around your exact
                            performance and budget needs.
                        </p>
                    </div>

                    {/* Card 4 */}
                    <div className="group bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-3xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all">
                        <div className="mb-5 w-12 h-12 rounded-xl bg-purple-500/20 text-purple-500 flex items-center justify-center text-xl font-bold">
                            💻
                        </div>
                        <h3 className="text-xl font-semibold mb-3 text-white">
                            Wide Product Range
                        </h3>
                        <p className="text-slate-300 leading-relaxed">
                            Apple, HP, Dell, Lenovo, Asus, MSI, Wacom, NVIDIA & more — all under one
                            trusted roof.
                        </p>
                    </div>

                    {/* Card 5 */}
                    <div className="group bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-3xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all">
                        <div className="mb-5 w-12 h-12 rounded-xl bg-orange-500/20 text-orange-500 flex items-center justify-center text-xl font-bold">
                            🤝
                        </div>
                        <h3 className="text-xl font-semibold mb-3 text-white">
                            Strong Brand Partnerships
                        </h3>
                        <p className="text-slate-300 leading-relaxed">
                            Authorized reseller relationships ensure genuine products, official
                            warranties, and reliable support.
                        </p>
                    </div>

                    {/* Card 6 */}
                    <div className="group bg-white/5 backdrop-blur-md border border-white/10 p-8 rounded-3xl shadow-lg hover:shadow-2xl transform hover:scale-105 transition-all">
                        <div className="mb-5 w-12 h-12 rounded-xl bg-teal-500/20 text-teal-500 flex items-center justify-center text-xl font-bold">
                            ⭐
                        </div>
                        <h3 className="text-xl font-semibold mb-3 text-white">
                            Excellent Local Support
                        </h3>
                        <p className="text-slate-300 leading-relaxed">
                            Honest pre-sales guidance, fast post-sales service, and quick local
                            repairs across Bangalore.
                        </p>
                    </div>

                    </div>
                </Container>
            </section>

            {/* TRUST + CTA SECTION */}
            <section className="relative py-24 bg-gradient-to-br from-black via-slate-900 to-black text-white overflow-hidden">

                {/* Background glows */}
                <div className="absolute -top-40 left-1/4 w-96 h-96 bg-blue-600/10 rounded-full blur-3xl"></div>
                <div className="absolute -bottom-40 right-1/4 w-96 h-96 bg-indigo-600/10 rounded-full blur-3xl"></div>

                <Container>
                    <div className="relative z-10 max-w-4xl mx-auto text-center">

                        {/* Badge */}
                        <div className="inline-block mb-6 px-5 py-2 rounded-full border border-white/20 text-sm text-blue-400 tracking-wide">
                            Trusted Since 2017
                        </div>

                        {/* Heading */}
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
                            Trusted by Hundreds of Studios <br className="hidden md:block" />
                            & Professionals
                        </h2>

                        {/* Description */}
                        <p className="mt-6 text-lg md:text-xl text-gray-300 leading-relaxed">
                            From freelancers to large enterprises, we deliver the same unwavering level
                            of quality, precision, and attention to detail. Partner with us and experience
                            the <span className="text-blue-400 font-semibold">Credible difference</span>.
                        </p>

                        {/* CTA */}
                        <div className="mt-10 flex justify-center gap-4 flex-wrap">
                            <a
                                href="https://wa.me/919538577199?text=Hello%2C%20I%20am%20interested%20in%20partnering%20with%20your%20company.%20Please%20share%20the%20partnership%20details%20and%20next%20steps."
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center justify-center px-10 py-4 rounded-xl font-semibold text-blue-600 bg-white hover:bg-gray-100 transition-all duration-300 shadow-lg hover:scale-105"
                                >
                                Partner With Us
                            </a>

                            {/* <a
                                href="/about"
                                className="inline-flex items-center justify-center px-10 py-4 rounded-xl font-semibold border border-white/30 text-white hover:bg-white/10 transition-all duration-300"
                            >
                                Learn More
                            </a> */}
                        </div>

                    </div>
                </Container>
            </section>

        </>
    );
}
